import React, {useCallback, useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {TreeGetSubcategoryData} from 'types'
import {useDidMount, useDidUpdate} from 'utils'
import {
  ActivityIndicator,
  Input,
  List,
  ListItemSelect,
  Menu,
} from 'common/components'
import convertUnit from 'lib/unit'
import {TreePlantTreeUppperFormProps} from '../Forms'

const StyledContainer = styled.div`
  margin-top: ${convertUnit(8)};
`

const StyledLoadingContainer = styled.div`
  display: flex;
  margin: 0 auto;
  padding: ${convertUnit(10)};
`

export default function TreePlantCategory({
  form,
}: TreePlantTreeUppperFormProps) {
  const {translate} = useTranslation()
  const [input, setInput] = useState('')
  const [data, setData] = useState<TreeGetSubcategoryData[]>()
  const [loading, setLoading] = useState(false)
  const stateToggle = useState(false)
  const setToggle = stateToggle[1]

  const {setValue, watch, register} = form
  const {fotoTreeCategory} = watch()

  const handleLoadData = useCallback(() => {
    setData([])
    setLoading(true)
    requestData('tree_get_fototree_subcategory', {
      params: {
        type: fotoTreeCategory.name,
      },
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status, data: {result}}) => {
        if (status === 200) setData(result.details)
      },
    })
  }, [fotoTreeCategory.name])

  const handleResetSubcategory = useCallback(() => {
    setValue('fotoTreeSubcategory', {})
    setInput('')
  }, [setValue])

  const handleRenderLoading = useMemo(
    () => (
      <StyledLoadingContainer>
        <ActivityIndicator iconColor="primary_5" />
      </StyledLoadingContainer>
    ),
    [],
  )

  const handleRenderItem = useCallback(
    (item: TreeGetSubcategoryData) => (
      <ListItemSelect
        title={item.name}
        onClick={() => {
          setToggle(false)
          setValue('fotoTreeSubcategory', item)
          setInput(item.name)
        }}
      />
    ),
    [setToggle, setValue],
  )

  const handleRenderList = useMemo(
    () => (
      <List
        data={
          !input
            ? data
            : data?.filter((item) =>
                item.name.toLowerCase().includes(input.toLowerCase()),
              )
        }
        keyExtractor={(item) => item.id}
        renderItem={handleRenderItem}
        listEmptyElement={loading ? handleRenderLoading : <></>}
      />
    ),
    [data, handleRenderLoading, handleRenderItem, input, loading],
  )

  const handleRenderInput = useMemo(
    () => (
      <Menu
        stateToggle={stateToggle}
        contentContainerStyle={{
          height: convertUnit(250),
          borderRadius: convertUnit(8),
        }}
        renderContent={handleRenderList}>
        <Input
          value={input}
          label={translate('tree:category')}
          placeholder={translate('tree:categoryPlaceholder')}
          rightIcon="delete"
          containerStyle={{marginBottom: convertUnit(4)}}
          onChangeText={(text) => {
            if (text.length < 1) {
              setValue('fotoTreeSubcategory', {})
            }
            setInput(text)
            setToggle(true)
          }}
          onFocus={() => setToggle(true)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              setToggle((prev) => !prev)
            }
          }}
          onRightIconClick={() => {
            setInput('')
            setValue('fotoTreeSubcategory', {})
          }}
        />
      </Menu>
    ),
    [handleRenderList, input, setToggle, setValue, stateToggle, translate],
  )

  useDidMount(() => {
    register('fotoTreeSubcategory')
  })

  useDidUpdate(handleResetSubcategory, [fotoTreeCategory])

  useEffect(handleLoadData, [handleLoadData])

  return <StyledContainer>{handleRenderInput}</StyledContainer>
}
